import type { ReactNode } from 'react';

import type {
  CampaignAnalysisAppendix,
  CampaignAnalysisMeta,
} from '@/entities/campaign/generated/campaign-type';
import { formatDateTime } from '@/shared/utils/date/date-formats';

import { formatWholeNumber, platformLogoItems } from '../../helpers/analysis-report';

export function ReportBackgroundList({
  appendix,
  meta,
}: {
  appendix: CampaignAnalysisAppendix;
  meta: CampaignAnalysisMeta;
}) {
  const backgroundRows: Array<{ label: string; value: ReactNode }> = [
    { label: 'Campaign name', value: meta.dataset.campaign_name },
    {
      label: 'Platforms',
      value: <PlatformLogoList platform={meta.dataset.platform} />,
    },
    {
      label: 'Collection period',
      value: `${formatDateTime(meta.period.start_date)} - ${formatDateTime(meta.period.end_date)}`,
    },
    {
      label: 'Post count',
      value: `${formatWholeNumber(meta.dataset.analyzed_post_count)} posts`,
    },
    { label: 'Target metric', value: appendix.metric_definition.target_metric },
  ];

  return (
    <dl className="grid w-full max-w-3xl gap-3 text-sm">
      {backgroundRows.map((row) => (
        <div
          key={row.label}
          className="grid gap-1 sm:grid-cols-[140px_minmax(0,1fr)] sm:items-center sm:gap-3 print:grid-cols-[32mm_minmax(0,1fr)]"
        >
          <dt className="text-muted-foreground font-medium">{row.label}</dt>
          <dd className="text-foreground min-w-0 font-medium wrap-break-word">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ReportAppendixSection({ appendix }: { appendix: CampaignAnalysisAppendix }) {
  const metric = appendix.metric_definition;

  return (
    <div className="grid max-w-3xl gap-8 text-sm">
      <section data-campaign-analysis-print-avoid-break className="grid gap-4">
        <p className="text-foreground text-base leading-6 font-semibold">Metric definition</p>
        <dl className="grid gap-3">
          <div className="grid gap-1 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
            <dt className="text-muted-foreground font-medium">Target metric</dt>
            <dd className="text-foreground leading-6">{metric.target_metric}</dd>
          </div>
          <div className="grid gap-1 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
            <dt className="text-muted-foreground font-medium">Method label</dt>
            <dd className="text-foreground leading-6">{metric.method_label}</dd>
          </div>
          <div className="grid gap-1 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
            <dt className="text-muted-foreground font-medium">Description</dt>
            <dd className="text-muted-foreground leading-6">{metric.description}</dd>
          </div>
        </dl>
      </section>

      <section data-campaign-analysis-print-avoid-break className="grid gap-4">
        <p className="text-foreground text-base leading-6 font-semibold">Filters</p>
        <ul className="text-muted-foreground grid list-disc gap-2 pl-5 leading-6">
          {appendix.filters.map((condition) => (
            <li key={condition}>{condition}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function PlatformLogoList({ platform }: { platform: string }) {
  if (platform.includes(':')) {
    return platform;
  }

  const normalizedPlatform = platform.toLowerCase();
  const matchedPlatforms = platformLogoItems.filter((platformLogoItem) =>
    normalizedPlatform.includes(platformLogoItem.key),
  );

  if (matchedPlatforms.length === 0) {
    return platform;
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1" title={platform}>
      {matchedPlatforms.map(({ label, Logo }) => (
        <span key={label} className="inline-flex items-center gap-1.5">
          <Logo aria-hidden="true" className="size-4 shrink-0" />
          <span>{label}</span>
        </span>
      ))}
    </span>
  );
}
